<template>
  <div class="form-result-frame-wrapper" :class="{ 'is-preview': isPreview }">
    <div
      v-if="!isPreview"
      class="resultframe-empty-state form-label text-start text-break fs-6"
    >
      Fügen Sie dieses Element dort ein, wo die Ergebnisse angezeigt werden
      sollen.
    </div>
    <div v-else class="resultframe-preview-content">
      <div v-if="isLoading" class="resultframe-loading">
        <LoadingSpinner />
      </div>
      <iframe
        ref="iframeRef"
        v-else-if="isHTMLResponse"
        :srcdoc="responsePayload"
        class="resultframe-iframe"
        @load="onIframeLoad"
      ></iframe>
      <div v-else-if="isJSONResponse" class="resultframe-json-viewer">
        <JsonViewer :value="responsePayload" />
      </div>
    </div>
  </div>
</template>

<script>
import JsonViewer from 'vue-json-viewer';
import LoadingSpinner from '@/components/widgets/LoadingSpinner.vue';
import {
  resolveResponseType,
  isHtmlResponseType,
  isJsonResponseType,
} from '@/utils/helpers/FormBuilderHelper';

export default {
  name: 'FormResultFrame',
  props: {
    isPreview: {
      type: Boolean,
      default: false,
    },
    responseData: {
      type: Object,
      default: () => ({
        responsePayload: null,
        responseType: null,
        isLoading: false,
      }),
    },
  },

  data() {
    return {
      resizeObserver: null,
    };
  },

  beforeDestroy() {
    this.detachIframeObservers();
  },

  methods: {
    onIframeLoad() {
      this.updateIframeHeight();
      this.attachIframeObservers();
    },

    updateIframeHeight() {
      const iframe = this.$refs.iframeRef;
      if (!iframe) return;
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc || !doc.body) return;

      iframe.style.height = 'auto';
      const height = doc.documentElement.scrollHeight;
      iframe.style.height = `${height + 2}px`;
    },

    attachIframeObservers() {
      this.detachIframeObservers();
      const iframe = this.$refs.iframeRef;
      if (!iframe) return;
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc || !doc.body) return;

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => {
          this.updateIframeHeight();
        });
        this.resizeObserver.observe(doc.documentElement);
      }
    },

    detachIframeObservers() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    },
  },

  computed: {
    responsePayload() {
      return (
        this.responseData?.responsePayload ?? this.responseData?.responseJson
      );
    },
    responseType() {
      return resolveResponseType(
        this.responseData?.responseType,
        this.responsePayload
      );
    },
    isHTMLResponse() {
      return isHtmlResponseType(this.responseType);
    },
    isJSONResponse() {
      return isJsonResponseType(this.responseType);
    },
    isLoading() {
      return this.responseData?.isLoading === true;
    },
  },
  components: {
    JsonViewer,
    LoadingSpinner,
  },
};
</script>

<style scoped>
.form-result-frame-wrapper {
  width: 100%;
  height: 100%;
}
.form-result-frame-wrapper.is-preview {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.resultframe-preview-content {
  width: 100%;
  height: 100%;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
}
.resultframe-empty-state {
  width: 100%;
  height: 100%;
  display: flex;
  font-family: 'OpenSans-Bold';
  color: #000000;
  line-height: 27px;
  letter-spacing: -0.02em;
  justify-content: center;
  text-align: center;
  align-items: center;
}
.resultframe-iframe {
  width: 100%;
  min-height: 0;
  display: block;
  border: none;
}
.resultframe-loading {
  width: 100%;
  height: 100%;
  min-height: 0;
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}
.resultframe-json-viewer {
  width: 100%;
  height: 100%;
  min-height: 0;
  flex: 1 1 auto;
  overflow: auto;
}
.resultframe-loading ::v-deep .loading {
  width: 100%;
  height: 100%;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
}
.resultframe-loading ::v-deep .progress-icon-wrapper {
  position: static;
  inset: auto;
  margin: 0;
}
.resultframe-loading ::v-deep .progress-icon {
  margin: 0 auto;
}
</style>
